import { gql, useQuery } from '@apollo/client';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocale } from 'models/localization';
import { shallowEqual, useSelector } from 'react-redux';
import { useCallback } from 'react';
import useJoinGroupChatSwitch from './useJoinGroupChatSwitch';

const GET_PRODUCT_BOOTH_DATA = gql`
  fragment BoothNameLocalizableString on LocalizableString {
    values(locales: $locales) {
      locale
      value
    }
  }

  query($boothId: ID!, $locales: [Locale!]!) {
    node(id: $boothId) {
      ... on Booth {
        meetupEnabled
        name {
          ...BoothNameLocalizableString
        }
        appointmentBookingUrl
      }
    }
  }
`;

const useProductBoothData = (boothId: string, productName: string) => {
  const locale = useSelector(selectLocale, shallowEqual);
  const { joinBoothChat } = useJoinGroupChatSwitch();
  const { loading, error, data } = useQuery(GET_PRODUCT_BOOTH_DATA, {
    variables: {
      boothId,
      locales: [locale],
    },
  });
  const { node: { appointmentBookingUrl, meetupEnabled, name: boothNameArray } } = data || { node: {} };
  const boothName = boothNameArray?.values?.reduce((acc, { locale: currentLocale, value }) => {
    acc[currentLocale] = value;
    return acc;
  }, {}) || {};
  const localizedBoothName = findAvailableLocale(boothName || {}, locale);

  const matomoBoothName = findAvailableLocale(boothName, 'en');

  const openChatWindow = useCallback(() => {
    window.trackingItemToFirebase(null, 'Booth', 'Chat', 'Group_Chat_Click', 'ProductItem', boothId, matomoBoothName);
    joinBoothChat(boothId, localizedBoothName);
  }, [boothId, joinBoothChat, localizedBoothName, matomoBoothName]);

  return {
    loading,
    error,
    appointmentBookingUrl,
    meetupEnabled,
    localizedBoothName,
    boothName,
    openChatWindow,
  };
};

export default useProductBoothData;
